import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type WidgetHostMessageVariant = 'loading' | 'error' | 'warning' | 'info';

export interface WidgetHostMessageProps {
  variant: WidgetHostMessageVariant;
  title: string;
  message?: string;
  /** Optional technical detail (HTTP status, error message, widget key). */
  detail?: string;
}

/**
 * Inline status shown inside a widget mount container (SSO, auth errors, etc.).
 */
export function WidgetHostMessage({
  variant,
  title,
  message,
  detail,
}: WidgetHostMessageProps) {
  const { t } = useTranslation();

  if (variant === 'loading') {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          p: 3,
          minHeight: 160,
        }}
        aria-busy='true'
        aria-live='polite'
      >
        <CircularProgress size={40} />
        <Typography
          variant='subtitle1'
          fontWeight={600}
          sx={{ width: '100%', textAlign: 'left' }}
        >
          {title}
        </Typography>
        {message ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ width: '100%', textAlign: 'left' }}
          >
            {message}
          </Typography>
        ) : null}
      </Box>
    );
  }

  const severity =
    variant === 'error' ? 'error' : variant === 'warning' ? 'warning' : 'info';

  return (
    <Box
      sx={{ width: '100%', p: 2 }}
      aria-live='polite'
    >
      <Alert
        severity={severity}
        sx={{
          width: '100%',
          alignItems: 'flex-start',
          '& .MuiAlert-message': {
            width: '100%',
            overflow: 'hidden',
          },
          '& .MuiAlert-icon': {
            alignSelf: 'flex-start',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            gap: 1,
          }}
        >
          <AlertTitle sx={{ m: 0, p: 0, width: '100%' }}>{title}</AlertTitle>
          {message ? (
            <Typography
              variant='body2'
              sx={{ width: '100%', textAlign: 'left' }}
            >
              {message}
            </Typography>
          ) : null}
          {detail ? (
            <Typography
              variant='caption'
              component='p'
              sx={{
                width: '100%',
                m: 0,
                textAlign: 'left',
                wordBreak: 'break-word',
              }}
              color='text.secondary'
            >
              {t('embeddableWidget.host.detailLabel', 'Details')}: {detail}
            </Typography>
          ) : null}
        </Box>
      </Alert>
    </Box>
  );
}
